<template>
  <form class="absoluteLabelsForm  contactForm__Form" @submit.prevent="submitForm">
    <h2>{{ $t('contactForm.formTitle') }}</h2>
    <p class="contactForm__FormDisclaimer">{{ $t('contactForm.formDisclaimer') }}</p>
    <div class="contactForm__FormSection">
      <h3>{{ $t('contactForm.formAccommodation') }}</h3>
      <SelectField
        :ref="addField"
        v-model="formData.accommodation"
        :options="properties"
        text-key="name"
        value-key="id"
        autocomplete
        :label="$t('contactForm.propertyName')"
        :errors="formErrors?.details?.accommodation"
      />
    </div>
    <div class="contactForm__FormSection">
      <h3>{{ $t('contactForm.formContact') }}</h3>
      <TextField :ref="addField" v-model="formData.first_name" :label="`${$t('forms.firstName')} *`" :errors="formErrors?.details?.first_name" :validate="{type: ['required', 'maxlength'], maxlength: 30, message: { required: $t('formErrors.required'), maxlength: $t('formErrors.maxLength', 30) }}"/>
      <TextField :ref="addField" v-model="formData.last_name" :label="`${$t('forms.lastName')} *`" :errors="formErrors?.details?.last_name" :validate="{type: ['required', 'maxlength'], maxlength: 30, message: { required: $t('formErrors.required'), maxlength: $t('formErrors.maxLength', 30) }}"/>
      <TextField :ref="addField" v-model="formData.email" type="email" :label="`${$t('forms.email')} *`" :errors="formErrors?.details?.email" :validate="{ type: ['required', 'email'], message: { required: $t('formErrors.required'), email: $t('formErrors.email') } }"/>
      <span>{{ $t('availabilityContact.confirmationEmail') }}</span>
      <TextField
        :ref="addField"
        v-model="formData.confirm_email"
        type="email"
        :label="`${$t('forms.confirmEmail')} *`"
        :validate="{
          type: ['required', 'email', 'sameAs'],
          compareValue: formData.email,
          message: { required: $t('formErrors.required'), email: $t('formErrors.email'), sameAs: $t('formErrors.sameAsEmail') },
        }"
      />
      <PhoneNumberField v-model:numberValue="phoneNumber.phone_number" v-model:codeValue="phoneNumber.dial_code" :label="$t('forms.mobile')"/>
    </div>
    <div class="contactForm__FormSection">
      <TextareaField
        :ref="addField"
        v-model="formData.message"
        :label="$t('modifyReservationForm.messageLabel')"
        :placeholder="$t('otherForm.messagePlaceholder')"
        :validate="{ type: 'required', message: { required: $t('formErrors.required') } }"
        :errors="formErrors?.details?.message"
      />
      <FileField
        :ref="addField"
        v-model="formData.attachments"
        :empty="$t('cancelReservationForm.uploadPlaceholder')"
        :label="$t('lostItemsForm.uploadLabel')"
        :info="$t('lostItemsForm.maxSize')"
        multiple
        :errors="formErrors?.details?.attachments"
      />
    </div>
    <div class="contactForm__FormSection">
      <CheckboxField
        :ref="addConditionalField"
        v-model="terms"
      >
        <i18n-t tag="p" scope="global" keypath="availabilityContact.checkbox">
          <template #linkConditions>
            <nuxt-link :to="link('GeneralConditions')" target="_blank">{{ $t('availabilityContact.linkConditions') }}</nuxt-link>
          </template>
          <template #linkPrivacy>
            <nuxt-link :to="link('PrivacyPolicy')" target="_blank">{{ $t('availabilityContact.linkPrivacy') }}</nuxt-link>
          </template>
        </i18n-t>
      </CheckboxField>
    </div>
    <Button :disabled="isButtonDisabled" type="button" class="contactForm__FormSubmit" medium full-width>{{ $t('submit') }}</Button>
    <Loader :active="isLoading" noText></Loader>
  </form>
</template>

<script setup>
  defineOptions({
    auth: 'aminess'
  });

  const props = defineProps({
    properties: {
      type: Array,
      default: [],
    },
  });

  const emit = defineEmits(['formSubmitted']);

  const { link } = useSetup();
  const { user: userData } = useAuth('aminess');
  const { t } = useI18n();

  const field = ref([]);
  const conditionalField = ref([]);
  const isButtonDisabled = ref(true);
  const isLoading = ref(false);
  const terms = ref(false);

  const phoneNumber = ref({
    phone_number: '',
    dial_code: '+385',
  });

  const formData = ref({
    first_name: '',
    last_name: '',
    email: '',
    confirm_email: '',
    phone: '',
    message: '',
    attachments: [],
  });
  const formErrors = ref({});

  function addField(el) {
    if (el && !el?.added) {
      field.value.push(el);
      el.added = true;
    }
  }

  function addConditionalField(el) {
    if (el && !el?.added) {
      conditionalField.value.push(el);
      el.added = true;
    }
  }

  function scrollToError() {
    setTimeout(() => {
      const elements = document.querySelectorAll('.formField.isError');
      if (elements.length) elements[0].scrollIntoView({ behavior: 'smooth' });
    }, 10);
  }

  async function submitForm() {
    let isValid = useFormIsValid(field.value);
    if (!isValid) {
      scrollToError();
      return;
    }
    isButtonDisabled.value = true;
    isLoading.value = true;
    try {
      const body = new FormData()
      const _data = JSON.parse(JSON.stringify(formData.value))
      delete _data.attachments
      for(const key in _data) body.append(key, _data[key])
      for(const file of formData.value.attachments) {
        body.append('attachments', file)
      }
      const response = await useClientFetch(`/contact-form/other/`, {
        method: 'POST',
        body: body,
      });
      emit('formSubmitted');
    }
    catch(e) {
      console.dir(e);
      if (e.data) formErrors.value = e.data;
      if(e.statusCode >= 500 || e.statusCode === undefined) {
        showError({
          statusCode: e.statusCode,
          statusMessage: 'Error',
        });
        window.scrollTo(0, 0);
      }
      scrollToError();
    }
    isButtonDisabled.value = false;
    isLoading.value = false;
  }

  watch(() => phoneNumber.value, (value) => {
    if (value.dial_code && value.phone_number) {
      formData.value.phone = `${value.dial_code}${value.phone_number}`;
    } else {
      formData.value.phone = '';
    }
  }, { deep: true, immediate: true });

  watch(() => terms.value, (value) => {
    isButtonDisabled.value = value ? false : true
  }, { deep: true, immediate: true });
</script>